import { TectonicAsset } from '@cronos-labs/tectonic-sdk/dist/types';
import { Text, Button, Icon } from '@cronos-labs/tectonic-ui-kit';
import { BigNumber } from 'ethers';
import { useState } from 'react';

import {
  formatUserUnderlyingAmount,
  formatUserUnderlyingAmountWithDust,
} from '@lib/units';
import AsCollateralToggle from '@components/AsCollateralToggle';
import Card, { CardProps } from '@components/Card';
import DataRow from '@components/DataRow';
import {
  getSupplyDailyEarningsAmount,
  getSupplyDailyEarningsRate,
} from '@lib/math';
import { formatRateToPercent, formatPercent } from '@lib/units';
import SupplyTtokenModal from '@components/SupplyTtokenModal';
import Features from '@components/Features';
import useDisableSupply from '@hooks/useDisableSupply';
import { TOKEN_INTEREST_INFOS } from '@config/TokenInterestInfo';
import { PoolType } from '@config/base';
import { BLACKLIST_COLLATERAL_SWAP_TOKENS } from '@config/constants';

import CardValue from './CardValue';

export interface YourSupplyCardProps {
  mode: PoolType;
  amount: BigNumber | null;
  asset: TectonicAsset;
  cardProps?: Omit<CardProps, 'children'>;
  hasBorrowedAsset: boolean | null;
  hasBorrowedAssetOnly?: boolean;
  isEnabledAsCollateral: boolean | null;
  loading: boolean;
  onAsCollateralClick(): void;
  onSupply(): void;
  onSwap(): void;
  onWithdraw(): void;
  supplyRatePerBlock: BigNumber | null;
  usdPrice: BigNumber | null;
  walletBalance: BigNumber | null;
  walletBalanceAsset: TectonicAsset | null;
}

function YourSupplyCard({
  mode,
  amount,
  asset,
  cardProps,
  hasBorrowedAsset,
  hasBorrowedAssetOnly,
  isEnabledAsCollateral,
  loading,
  onAsCollateralClick,
  onSupply,
  onSwap,
  onWithdraw,
  supplyRatePerBlock,
  usdPrice,
  walletBalance,
  walletBalanceAsset,
}: YourSupplyCardProps): JSX.Element {
  const dailyEarningsAmount =
    amount && supplyRatePerBlock
      ? getSupplyDailyEarningsAmount(supplyRatePerBlock, amount)
      : null;
  const dailyEarningsRate =
    amount && dailyEarningsAmount && usdPrice
      ? getSupplyDailyEarningsRate(asset, dailyEarningsAmount, amount, usdPrice)
      : null;
  const disableSupply = useDisableSupply(mode, asset);
  const { symbol } = asset;
  const [infoModalOpen, setInfoModalOpen] = useState(false);

  const disabledUseAsCollateral =
    !isEnabledAsCollateral &&
    TOKEN_INTEREST_INFOS[mode][asset?.symbol]?.collateralFactor === 0;

  return (
    <Card {...(cardProps ?? {})}>
      <Features.TToken>
        {SupplyTtokenModal({ infoModalOpen, setInfoModalOpen })}
      </Features.TToken>
      <div className="mb-3">
        <Text variant="heading3" className="inline">
          Your supply
        </Text>
        <div className="inline-flex flex-wrap content-center">
          <Features.TToken>
            <Icon.Info
              className="ml-1 inline h-2 w-2 cursor-pointer text-yellowPrimary"
              onClick={() => setInfoModalOpen(true)}
            />
          </Features.TToken>
        </div>
      </div>
      <div className="space-y-2">
        <DataRow
          label={<Text>Currently supplying</Text>}
          value={
            <CardValue
              loading={loading}
              value={
                amount && (
                  <Text className="font-semibold">
                    {`${formatUserUnderlyingAmountWithDust(asset, amount)} ${
                      asset.symbol
                    }`}
                  </Text>
                )
              }
            />
          }
        />
        <DataRow
          label={<Text>Daily earnings</Text>}
          value={
            <CardValue
              loading={loading}
              value={
                !!(dailyEarningsAmount && dailyEarningsRate) && (
                  <Text className="font-semibold">
                    {`${formatUserUnderlyingAmount(
                      asset,
                      dailyEarningsAmount
                    )} ${asset.symbol} (${formatPercent(
                      formatRateToPercent(dailyEarningsRate)
                    )})`}
                  </Text>
                )
              }
            />
          }
        />
        <DataRow
          label={<Text>Use as collateral</Text>}
          value={
            <CardValue
              loading={loading}
              value={
                !!(
                  isEnabledAsCollateral !== null && hasBorrowedAsset !== null
                ) && (
                  <div className="h-3">
                    <AsCollateralToggle
                      disabled={disabledUseAsCollateral}
                      isEnabled={isEnabledAsCollateral}
                      onClick={onAsCollateralClick}
                      tooltipMessage={
                        hasBorrowedAsset
                          ? `${symbol} must be enabled as collateral when borrowed. Repay ${symbol} in order to disable as collateral.`
                          : null
                      }
                    />
                  </div>
                )
              }
            />
          }
        />
        <DataRow
          label={<Text>Wallet balance</Text>}
          value={
            <CardValue
              loading={loading}
              value={
                !!(walletBalanceAsset && walletBalance) && (
                  <Text className="font-semibold">
                    {`${formatUserUnderlyingAmount(
                      walletBalanceAsset,
                      walletBalance
                    )} ${walletBalanceAsset.symbol}`}
                  </Text>
                )
              }
            />
          }
        />
      </div>

      <div className="mt-3 flex items-center justify-center gap-7 desktop:flex-row mobile:gap-2">
        <Button
          className="!border-0 !px-0 mobile:flex-1"
          disabled={
            loading ||
            hasBorrowedAssetOnly ||
            disableSupply ||
            disabledUseAsCollateral
          }
          size="small"
          variant="outlined"
          onClick={onSupply}
        >
          <div>
            <Icon.Supply className="#F4F499 inline" width="32" height="32" />
            <div className="text-xs font-light">Supply</div>
          </div>
        </Button>

        {!BLACKLIST_COLLATERAL_SWAP_TOKENS.includes(symbol) && (
          <Button
            className="!border-0 !px-0 mobile:flex-1"
            disabled={loading || hasBorrowedAssetOnly}
            size="small"
            variant="outlined"
            onClick={onSwap}
          >
            <div>
              <Icon.Swap className="#F4F499 inline" width="32" height="32" />
              <div className="text-xs font-light">Swap</div>
            </div>
          </Button>
        )}

        <Button
          className="ml-[-5px] !border-0 !px-0 mobile:flex-1"
          disabled={loading || hasBorrowedAssetOnly}
          size="small"
          variant="outlined"
          onClick={onWithdraw}
        >
          <div>
            <Icon.Withdraw className="#F4F499 inline" width="32" height="32" />
            <div className="text-xs font-light">Withdraw</div>
          </div>
        </Button>
      </div>
      {(disableSupply || disabledUseAsCollateral) && (
        <Text className="mt-0.5 text-small">
          You are unable to deposit your tokens as the the maximum amount that
          can be supplied to this market has been reached.
        </Text>
      )}
      {hasBorrowedAssetOnly && (
        <div className="mt-2 flex gap-1">
          <div>
            <Icon.Info className="text-yellowPrimary" width={18} height={18} />
          </div>
          <Text className="text-mediumSmall">
            Since you have already borrowed {asset.symbol}, supplying is
            disabled.
          </Text>
        </div>
      )}
      {asset.symbol === 'LCRO' && mode === 'VENO' && (
        <div className="mt-1 flex gap-1">
          <div>
            <Icon.Info className="text-yellowPrimary" width={18} height={18} />
          </div>
          <div className="text-mediumSmall text-white60">
            Note: Depositing your tLCRO in Veno&apos;s Tectonic Garden will
            reduce your LCRO collateral balance. If you have borrowed against
            your LCRO, this puts you at risk of liquidation
          </div>
        </div>
      )}
    </Card>
  );
}

export default YourSupplyCard;
